import { Checkbox } from "primereact/checkbox";
import { VirtualScroller, VirtualScrollerProps } from "primereact/virtualscroller";
import { useState } from "react";

import { CenterLayout, Result } from "@/components";
import { SpinnerIcon } from "@/components/atoms/icons";
import { Checkbox as CheckboxUrbix } from "@/components/atoms/ui/checkbox/index";
import { cn } from "@/styles/cn";

import { type Option, type OptionValue } from "../../types/option";
import {
  Divider,
  DividerItem,
  getItemHeight,
  getValueKey,
  SelectFilter,
  SelectItem,
  useFilterOptions,
  useSortSelectOnTop,
  type SelectItemVariantsProps,
} from "../SelectBase";

import { isSelected } from "./helpers";
import { useSelectionMultiple } from "./hooks/useSelectionMultiple";

export type SelectMultipleinlineProps<T extends Option> = SelectItemVariantsProps & {
  className?: string;
  listClassName?: string;
  selectItemClassName?: string;
  loading?: boolean;
  optionLabel?: string;
  optionValue?: string;
  dataKey: string;
  filter?: boolean;
  filterCustom?: (opt: T, query: string) => boolean;
  options: Array<T>;
  value?: OptionValue<T>[];
  disabledOptions?: T[];
  showSelectedOnTop?: boolean;
  showSelecteAll?: boolean;
  selectAllLabel?: string;
  /**
   * "invalidateSortCounter" Contador numerico que sirve para controlar
   * cuando se dispara el sort.
   * Util cuando se usa el showSelectedOnTop para evitar que se reordenen los options
   * cuando cambia la selección
   */
  invalidateSortCounter?: number;
  placeholder?: string;
  itemTemplate?: (option: T) => React.ReactNode;
  itemSize?: VirtualScrollerProps["itemSize"];
  onChange?: (value: OptionValue<T>[]) => void;
};

function SelectMultipleinline<TValue extends Option>({
  className,
  listClassName,
  selectItemClassName,
  itemSize,
  loading,
  optionLabel = "label",
  optionValue,
  dataKey,
  itemTemplate,
  filter = true,
  filterCustom,
  options,
  value,
  disabledOptions,
  showSelectedOnTop = true,
  invalidateSortCounter,
  showSelecteAll = false,
  selectAllLabel = "Seleccionar todos",
  size = "md",
  placeholder,
  onChange,
}: Readonly<SelectMultipleinlineProps<TValue>>) {
  const sortedOptions = useSortSelectOnTop<TValue>({
    options,
    invalidateSortCounter,
    dataKey,
    optionLabel,
    optionValue,
    value,
    showSelectedOnTop,
  });

  const [filterValue, setFilterValue] = useState("");
  const {
    filtered: filteredOptions,
    hasFilterApplied,
    isEmptyResult,
  } = useFilterOptions({
    query: filterValue,
    optionLabel,
    options: sortedOptions,
    filterCustom,
  });

  const { isAllSelected, isPartialSelected, selected, doToggleSelectAll, doSelect } =
    useSelectionMultiple({
      options,
      dataKey,
      optionLabel,
      optionValue,
      value,
      disabledOptions,
      onChange,
    });

  const onInputValueChange = (value: string) => {
    setFilterValue(value);
  };

  const checkList = (opt: TValue | Divider) => {
    if (opt instanceof Divider) {
      return <DividerItem />;
    }
    const label = opt[optionLabel];
    const valueKey = getValueKey(opt, { optionLabel, dataKey });
    const checked = isSelected(opt, selected, { dataKey, optionLabel });
    const disabled = isSelected(opt, disabledOptions, { dataKey, optionLabel });
    const inputId = `checkList-${valueKey}`;

    return (
      <SelectItem
        key={valueKey}
        className={selectItemClassName}
        disabled={disabled}
        size={size}
        title={label}
      >
        <Checkbox
          checked={checked}
          className="flex h-[1.31rem] items-center"
          disabled={disabled}
          inputId={inputId}
          onChange={(e) => doSelect(opt, e.checked === true)}
        />
        <SelectItem.Text>{itemTemplate ? itemTemplate(opt) : label}</SelectItem.Text>
      </SelectItem>
    );
  };

  if (loading) {
    return (
      <CenterLayout className="min-h-1 pt-0">
        <SpinnerIcon className="animate-spin" />
      </CenterLayout>
    );
  }

  return (
    <div className={cn("flex h-full min-h-1 w-full flex-col gap-1", className)}>
      {filter && (
        <SelectFilter
          invalid={isEmptyResult}
          placeholder={placeholder}
          onChange={onInputValueChange}
        />
      )}
      {isEmptyResult && <Result.Empty variant="textInline" />}

      {!isEmptyResult && (
        <div className={cn("flex h-full min-h-1 flex-col", listClassName)}>
          {showSelecteAll && !hasFilterApplied && (
            <ul className="border-b border-solid border-urbix-gray-300">
              <SelectItem className="my-1" size={size}>
                <CheckboxUrbix
                  checked={isAllSelected || isPartialSelected}
                  className="mr-1"
                  partialChecked={isPartialSelected}
                  onCheckedChange={doToggleSelectAll}
                />
                <SelectItem.Text>{selectAllLabel}</SelectItem.Text>
              </SelectItem>
            </ul>
          )}

          <ul className="relative mt-1 flex h-full w-full flex-col overflow-hidden">
            <VirtualScroller
              autoSize
              className="border-1 surface-border border-round absolute inset-0 h-full min-h-[100px] w-full flex-1 overflow-auto p-2"
              itemSize={itemSize ?? getItemHeight(size)}
              itemTemplate={checkList}
              items={filteredOptions}
              pt={{
                content: ["right-0"],
              }}
              showLoader={false}
            />
          </ul>
        </div>
      )}
    </div>
  );
}

export { SelectMultipleinline };
